import React, { useState, useEffect } from 'react';
import { 
  CreditCard, DollarSign, FileText, Download, CheckCircle, 
  Clock, AlertCircle, TrendingUp, Users, Calendar, Sparkles,
  ChevronRight, ExternalLink
} from 'lucide-react';
import { Button } from '../components/ui/button';
import api from '../utils/api';
import { toast } from 'sonner';

// Subscription Plans Component
export const SubscriptionPlans = ({ onSelectPlan, currentPlan }) => {
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await api.getSubscriptionPlans();
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-white/60">Loading plans...</div>;
  }

  const planOrder = ['basic', 'pro', 'enterprise'];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {planOrder.map((code) => {
        const plan = plans[code];
        if (!plan) return null;
        
        const isCurrentPlan = currentPlan === code;
        const isPro = code === 'pro';
        
        return (
          <div
            key={code}
            className={`relative bg-[#0A0A0F] border rounded-lg p-6 ${
              isPro ? 'border-[#D4AF37]' : 'border-white/10'
            } ${isCurrentPlan ? 'ring-2 ring-[#D4AF37]' : ''}`}
          >
            {isPro && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <span className="bg-[#D4AF37] text-black text-xs font-bold px-3 py-1 rounded-full">
                  POPULAR
                </span>
              </div>
            )}
            
            <h3 className="text-xl font-medium mb-2">{plan.name}</h3>
            <div className="flex items-baseline mb-4">
              <span className="text-3xl font-bold text-[#D4AF37]">₦{plan.amount.toLocaleString()}</span>
              <span className="text-white/60 ml-2">/ {plan.interval}</span>
            </div>
            
            <ul className="space-y-3 mb-6">
              {plan.features.map((feature, i) => (
                <li key={i} className="flex items-start text-sm text-white/70">
                  <CheckCircle className="text-[#D4AF37] mr-2 mt-0.5 flex-shrink-0" size={16} />
                  {feature}
                </li>
              ))}
            </ul>
            
            <Button
              onClick={() => onSelectPlan(code)}
              disabled={isCurrentPlan}
              className={`w-full ${
                isPro
                  ? 'bg-[#D4AF37] hover:bg-[#B8963A] text-black'
                  : 'bg-white/10 hover:bg-white/20 text-white'
              }`}
            >
              {isCurrentPlan ? 'Current Plan' : 'Select Plan'}
            </Button>
          </div>
        );
      })}
    </div>
  );
};

// Invoice List Component
export const InvoiceList = ({ invoices, onPayInvoice, onDownloadPdf }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-500/20 text-green-400';
      case 'sent': return 'bg-yellow-500/20 text-yellow-400';
      case 'overdue': return 'bg-red-500/20 text-red-400';
      default: return 'bg-white/10 text-white/60';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return <CheckCircle size={16} />;
      case 'sent': return <Clock size={16} />;
      case 'overdue': return <AlertCircle size={16} />;
      default: return <FileText size={16} />;
    }
  };

  return (
    <div className="space-y-4">
      {invoices.length === 0 ? (
        <div className="text-center py-8 text-white/60">
          <FileText className="mx-auto mb-3 opacity-40" size={48} />
          <p>No invoices found</p>
        </div>
      ) : (
        invoices.map((invoice) => (
          <div
            key={invoice.invoice_id}
            className="bg-[#0A0A0F] border border-white/5 rounded-lg p-4 hover:border-[#D4AF37]/30 transition-all"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 bg-white/5 rounded-lg flex items-center justify-center">
                  <FileText className="text-[#D4AF37]" size={20} />
                </div>
                <div>
                  <h4 className="font-medium">{invoice.invoice_number}</h4>
                  <p className="text-sm text-white/60">{invoice.description || 'Event Services'}</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-4">
                <div className="text-right">
                  <p className="font-bold text-[#D4AF37]">
                    ₦{invoice.amount?.toLocaleString()}
                  </p>
                  <p className="text-xs text-white/40">Due: {invoice.due_date || 'N/A'}</p>
                </div>
                
                <span className={`flex items-center px-3 py-1 rounded-full text-xs ${getStatusColor(invoice.status)}`}>
                  {getStatusIcon(invoice.status)}
                  <span className="ml-1">{invoice.status?.toUpperCase()}</span>
                </span>
                
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => onDownloadPdf(invoice.invoice_id)}
                    className="text-white/60 hover:text-white"
                  >
                    <Download size={16} />
                  </Button>
                  
                  {invoice.status !== 'paid' && (
                    <Button
                      size="sm"
                      onClick={() => onPayInvoice(invoice)}
                      className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
                    >
                      Pay Now
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))
      )}
    </div>
  );
};

// Transaction History Component
export const TransactionHistory = ({ transactions }) => {
  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'pending': return 'text-yellow-400';
      case 'failed': return 'text-red-400';
      case 'refunded': return 'text-orange-400';
      default: return 'text-white/60';
    }
  };

  return (
    <div className="bg-[#0A0A0F] border border-white/5 rounded-lg overflow-hidden">
      <table className="w-full">
        <thead className="bg-white/5">
          <tr>
            <th className="text-left px-4 py-3 text-sm font-medium text-white/60">Date</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-white/60">Type</th>
            <th className="text-left px-4 py-3 text-sm font-medium text-white/60">Provider</th>
            <th className="text-right px-4 py-3 text-sm font-medium text-white/60">Amount</th>
            <th className="text-center px-4 py-3 text-sm font-medium text-white/60">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-white/5">
          {transactions.length === 0 ? (
            <tr>
              <td colSpan={5} className="text-center py-8 text-white/60">
                No transactions yet
              </td>
            </tr>
          ) : (
            transactions.map((tx) => (
              <tr key={tx.transaction_id} className="hover:bg-white/5">
                <td className="px-4 py-3 text-sm">
                  {new Date(tx.created_at).toLocaleDateString()}
                </td>
                <td className="px-4 py-3 text-sm capitalize">
                  {tx.payment_type?.replace('_', ' ')}
                </td>
                <td className="px-4 py-3 text-sm capitalize">
                  {tx.payment_provider}
                </td>
                <td className="px-4 py-3 text-sm text-right font-medium">
                  {tx.currency} {tx.amount?.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-center">
                  <span className={`text-sm capitalize ${getStatusColor(tx.status)}`}>
                    {tx.status}
                  </span>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// Finance Dashboard (Admin)
export const FinanceDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFinanceData();
  }, []);

  const fetchFinanceData = async () => {
    try {
      const response = await api.getFinancialDashboard();
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch finance data:', error);
      toast.error('Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <DollarSign className="text-[#D4AF37]" size={24} />
            <TrendingUp className="text-green-400" size={16} />
          </div>
          <p className="text-2xl font-bold">₦{(data?.total_revenue || 0).toLocaleString()}</p>
          <p className="text-sm text-white/60">Total Revenue</p>
        </div>
        
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <Clock className="text-yellow-400" size={24} />
          </div>
          <p className="text-2xl font-bold">₦{(data?.outstanding || 0).toLocaleString()}</p>
          <p className="text-sm text-white/60">Outstanding</p>
        </div>
        
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <Users className="text-blue-400" size={24} />
          </div>
          <p className="text-2xl font-bold">{data?.active_subscriptions || 0}</p>
          <p className="text-sm text-white/60">Active Subscriptions</p>
        </div>
        
        <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded-lg">
          <div className="flex items-center justify-between mb-4">
            <FileText className="text-green-400" size={24} />
          </div>
          <p className="text-2xl font-bold">{data?.paid_invoices || 0}</p>
          <p className="text-sm text-white/60">Paid Invoices</p>
        </div>
      </div>

      {/* Revenue by Month Chart (simplified) */}
      <div className="bg-[#0A0A0F] border border-white/5 p-6 rounded-lg">
        <h3 className="text-lg font-medium mb-4">Monthly Revenue Trend</h3>
        <div className="h-64 flex items-end space-x-2">
          {(data?.revenue_by_month || []).slice(0, 12).reverse().map((item, i) => {
            const maxRevenue = Math.max(...(data?.revenue_by_month || []).map(r => r.revenue || 0), 1);
            const height = ((item.revenue || 0) / maxRevenue) * 100;
            return (
              <div key={i} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-[#D4AF37] rounded-t transition-all"
                  style={{ height: `${height}%`, minHeight: '4px' }}
                />
                <span className="text-xs text-white/40 mt-2">{item.month?.slice(5)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Payment Modal Component
export const PaymentModal = ({ invoice, onClose, onSuccess }) => {
  const [method, setMethod] = useState('stripe');
  const [loading, setLoading] = useState(false);

  const handlePayment = async () => {
    setLoading(true);
    try {
      const originUrl = window.location.origin;
      
      let response;
      if (method === 'stripe') {
        response = await api.createStripeCheckout({
          invoice_id: invoice.invoice_id,
          payment_method: 'stripe',
          origin_url: originUrl
        });
        
        if (response.data.checkout_url) {
          window.location.href = response.data.checkout_url;
        }
      } else {
        response = await api.initializePaystackPayment(invoice.invoice_id);
        
        if (response.data.authorization_url) {
          window.location.href = response.data.authorization_url;
        }
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error('Failed to initialize payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
      <div className="bg-[#0A0A0F] border border-white/10 rounded-lg p-6 w-full max-w-md">
        <h2 className="text-xl font-medium mb-4">Pay Invoice</h2>
        
        <div className="bg-white/5 p-4 rounded-lg mb-6">
          <p className="text-sm text-white/60">Invoice</p>
          <p className="font-bold">{invoice.invoice_number}</p>
          <p className="text-2xl font-bold text-[#D4AF37] mt-2">
            ₦{invoice.amount?.toLocaleString()}
          </p>
        </div>
        
        <div className="space-y-3 mb-6">
          <p className="text-sm text-white/60">Select payment method</p>
          
          <button
            onClick={() => setMethod('stripe')}
            className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all ${
              method === 'stripe'
                ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                : 'border-white/10 hover:border-white/20'
            }`}
          >
            <div className="flex items-center">
              <CreditCard className="mr-3 text-[#D4AF37]" size={20} />
              <div className="text-left">
                <p className="font-medium">Card Payment (Stripe)</p>
                <p className="text-xs text-white/60">International cards accepted</p>
              </div>
            </div>
            {method === 'stripe' && <CheckCircle className="text-[#D4AF37]" size={20} />}
          </button>
          
          <button
            onClick={() => setMethod('paystack')}
            className={`w-full flex items-center justify-between p-4 rounded-lg border transition-all ${
              method === 'paystack'
                ? 'border-[#D4AF37] bg-[#D4AF37]/10'
                : 'border-white/10 hover:border-white/20'
            }`}
          >
            <div className="flex items-center">
              <DollarSign className="mr-3 text-green-400" size={20} />
              <div className="text-left">
                <p className="font-medium">Paystack (NGN)</p>
                <p className="text-xs text-white/60">Nigerian bank cards & transfers</p>
              </div>
            </div>
            {method === 'paystack' && <CheckCircle className="text-[#D4AF37]" size={20} />}
          </button>
        </div>
        
        <div className="flex space-x-3">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-white/20"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePayment}
            disabled={loading}
            className="flex-1 bg-[#D4AF37] hover:bg-[#B8963A] text-black"
          >
            {loading ? 'Processing...' : 'Continue to Payment'}
            <ExternalLink size={16} className="ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Payment Success Page Component
export const PaymentSuccess = () => {
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    
    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await api.getStripePaymentStatus(sessionId);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(response.data);
        toast.success('Payment successful!');
        return;
      } else if (response.data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setStatus('pending');
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setStatus('error');
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
    
  }, []);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <div className="bg-[#0A0A0F] border border-white/10 rounded-lg p-8 max-w-md text-center">
        {status === 'checking' && (
          <>
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#D4AF37] mx-auto mb-4"></div>
            <h2 className="text-xl font-medium mb-2">Checking payment status...</h2>
            <p className="text-white/60">Please wait while we confirm your payment.</p>
          </>
        )}
        
        {status === 'pending' && (
          <>
            <Clock className="text-yellow-400 mx-auto mb-4" size={64} />
            <h2 className="text-xl font-medium mb-2">Processing Payment</h2>
            <p className="text-white/60">Your payment is being processed...</p>
          </>
        )}
        
        {status === 'success' && (
          <>
            <CheckCircle className="text-green-400 mx-auto mb-4" size={64} />
            <h2 className="text-xl font-medium mb-2">Payment Successful!</h2>
            <p className="text-white/60 mb-4">Thank you for your payment.</p>
            {paymentData && (
              <div className="bg-white/5 p-4 rounded-lg mb-4">
                <p className="text-2xl font-bold text-[#D4AF37]">
                  {paymentData.currency?.toUpperCase()} {paymentData.amount?.toLocaleString()}
                </p>
              </div>
            )}
            <Button
              onClick={() => window.location.href = '/dashboard/client'}
              className="bg-[#D4AF37] hover:bg-[#B8963A] text-black"
            >
              Go to Dashboard
            </Button>
          </>
        )}
        
        {status === 'error' && (
          <>
            <AlertCircle className="text-red-400 mx-auto mb-4" size={64} />
            <h2 className="text-xl font-medium mb-2">Something went wrong</h2>
            <p className="text-white/60 mb-4">We could not verify your payment. Please contact support.</p>
            <Button
              onClick={() => window.location.href = '/dashboard/client'}
              className="bg-white/10 hover:bg-white/20"
            >
              Return to Dashboard
            </Button>
          </>
        )}
        
        {status === 'timeout' && (
          <>
            <Clock className="text-orange-400 mx-auto mb-4" size={64} />
            <h2 className="text-xl font-medium mb-2">Verification Timeout</h2>
            <p className="text-white/60 mb-4">Payment verification is taking longer than expected. You will receive a confirmation email.</p>
            <Button
              onClick={() => window.location.href = '/dashboard/client'}
              className="bg-white/10 hover:bg-white/20"
            >
              Return to Dashboard
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default {
  SubscriptionPlans,
  InvoiceList,
  TransactionHistory,
  FinanceDashboard,
  PaymentModal,
  PaymentSuccess
};
